import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  Plus, Pencil, Trash2, RotateCcw, Eye, EyeOff, AlertTriangle,
  Clock, Car, Handshake, UserCircle, ClipboardList, ChevronLeft,
  ChevronRight, RefreshCw, Megaphone, Receipt, ChevronDown, ChevronUp, UserPlus
} from "lucide-react";
import { Tables } from "@/integrations/supabase/types";

type AuditEntry = Tables<"audit_log">;

const ACTION_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  created: { label: "Created", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", icon: Plus },
  updated: { label: "Updated", color: "text-blue-700", bg: "bg-blue-50 border-blue-200", icon: Pencil },
  deleted: { label: "Deleted", color: "text-red-700", bg: "bg-red-50 border-red-200", icon: Trash2 },
  restored: { label: "Restored", color: "text-violet-700", bg: "bg-violet-50 border-violet-200", icon: RotateCcw },
  permanently_deleted: { label: "Permanently Deleted", color: "text-red-900", bg: "bg-red-100 border-red-300", icon: AlertTriangle },
  hidden: { label: "Hidden", color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: EyeOff },
  unhidden: { label: "Unhidden", color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: Eye },
  status_changed: { label: "Status Changed", color: "text-blue-700", bg: "bg-blue-50 border-blue-200", icon: RefreshCw },
  converted: { label: "Converted", color: "text-green-700", bg: "bg-green-50 border-green-200", icon: UserPlus },
  captured: { label: "Captured", color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", icon: Megaphone },
};

const ENTITY_CONFIG: Record<string, { label: string; icon: any }> = {
  vehicle: { label: "Vehicle", icon: Car },
  deal: { label: "Deal", icon: Handshake },
  customer: { label: "Customer", icon: UserCircle },
  lead: { label: "Lead", icon: Megaphone },
  expense: { label: "Expense", icon: Receipt },
};

const PAGE_SIZE = 50;

// Use Eastern Time for a Canadian dealership
const TIMEZONE = "America/Toronto";

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  // Filters
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");

  // User email cache
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});

  // Expanded entries (for showing changes)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadEntries();
  }, [page, entityFilter, actionFilter]);

  const loadEntries = async () => {
    setIsLoading(true);

    let query = supabase
      .from("audit_log")
      .select("*", { count: "exact" })
      .order("performed_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (entityFilter !== "all") {
      query = query.eq("entity_type", entityFilter);
    }
    if (actionFilter !== "all") {
      query = query.eq("action", actionFilter);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Failed to load audit log:", error);
    } else {
      setEntries(data || []);
      setTotalCount(count || 0);

      // Load user emails for performers
      const performerIds = [...new Set((data || []).map((e) => e.performed_by).filter(Boolean))] as string[];
      const unknownIds = performerIds.filter((id) => !userEmails[id]);

      if (unknownIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, email, full_name")
          .in("id", unknownIds);

        if (profiles) {
          const newEmails: Record<string, string> = {};
          profiles.forEach((p) => {
            newEmails[p.id] = p.full_name || p.email;
          });
          setUserEmails((prev) => ({ ...prev, ...newEmails }));
        }
      }
    }
    setIsLoading(false);
  };

  const getActionConfig = (action: string) => {
    return ACTION_CONFIG[action] || { label: action, color: "text-gray-700", bg: "bg-gray-50 border-gray-200", icon: ClipboardList };
  };

  const getEntityConfig = (entityType: string) => {
    return ENTITY_CONFIG[entityType] || { label: entityType, icon: ClipboardList };
  };

  const getDescription = (entry: AuditEntry): string => {
    const meta = entry.metadata as any;
    if (meta?.name) return meta.name;
    if (meta?.vin) return `VIN: ${meta.vin}`;
    if (meta?.title) return meta.title;
    if (meta?.customer_name) return meta.customer_name;
    if (meta?.lead_name) return meta.lead_name;
    if (meta?.category) return `${meta.category}${meta.amount ? ` — $${meta.amount}` : ""}`;
    return entry.entity_id || "Unknown";
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();

    // Compare dates in Eastern Time to fix "today"/"yesterday" logic
    const dateInTZ = d.toLocaleDateString("en-CA", { timeZone: TIMEZONE });
    const nowInTZ = now.toLocaleDateString("en-CA", { timeZone: TIMEZONE });

    // Yesterday in timezone
    const yesterdayDate = new Date(now);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayInTZ = yesterdayDate.toLocaleDateString("en-CA", { timeZone: TIMEZONE });

    const timeStr = d.toLocaleTimeString("en-CA", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: TIMEZONE,
    });

    if (dateInTZ === nowInTZ) {
      return `${timeStr} today`;
    }
    if (dateInTZ === yesterdayInTZ) {
      return `${timeStr} yesterday`;
    }

    return d.toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: TIMEZONE,
    }) + " at " + timeStr;
  };

  const renderChanges = (changes: any) => {
    if (!changes || typeof changes !== "object") return null;
    const entries = Object.entries(changes);
    if (entries.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {entries.map(([field, val]: [string, any]) => {
          if (!val || typeof val !== "object") return null;
          const { from, to } = val as { from: unknown; to: unknown };
          const formatVal = (v: unknown) => {
            if (v === null || v === undefined) return "—";
            if (typeof v === "boolean") return v ? "Yes" : "No";
            return String(v);
          };
          const fieldLabel = field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          return (
            <div key={field} className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground font-medium min-w-[100px]">{fieldLabel}:</span>
              <span className="text-red-600 line-through">{formatVal(from)}</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-green-700 font-medium">{formatVal(to)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const hasChanges = (entry: AuditEntry): boolean => {
    const changes = entry.changes as any;
    return changes && typeof changes === "object" && Object.keys(changes).length > 0;
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">Audit Log</h1>
        <p className="text-muted-foreground">Complete history of all admin actions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Entity:</span>
          <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="vehicle">Vehicles</SelectItem>
              <SelectItem value="deal">Deals</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Action:</span>
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="updated">Updated</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="restored">Restored</SelectItem>
              <SelectItem value="permanently_deleted">Permanently Deleted</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
              <SelectItem value="unhidden">Unhidden</SelectItem>
              <SelectItem value="status_changed">Status Changed</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="captured">Captured</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto text-sm text-muted-foreground">
          {totalCount} total entr{totalCount === 1 ? "y" : "ies"}
        </div>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="text-center py-12">Loading audit log...</div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <ClipboardList className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No audit entries</h3>
            <p className="text-muted-foreground text-sm">
              Actions will be recorded here as you manage your inventory.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const actionCfg = getActionConfig(entry.action);
            const entityCfg = getEntityConfig(entry.entity_type);
            const ActionIcon = actionCfg.icon;
            const EntityIcon = entityCfg.icon;
            const description = getDescription(entry);
            const performer = entry.performed_by ? userEmails[entry.performed_by] || "Admin" : "System";
            const showChanges = hasChanges(entry);
            const isExpanded = expandedIds.has(entry.id);

            return (
              <Card key={entry.id} className="border hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Action icon */}
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border ${actionCfg.bg}`}>
                      <ActionIcon className={`h-4 w-4 ${actionCfg.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-sm">{performer}</span>
                        <Badge variant="outline" className={`text-xs ${actionCfg.bg} ${actionCfg.color} border`}>
                          {actionCfg.label}
                        </Badge>
                        <span className="text-sm text-muted-foreground">a</span>
                        <span className="inline-flex items-center gap-1 text-sm">
                          <EntityIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{entityCfg.label}</span>
                        </span>
                      </div>

                      <p className="text-sm text-muted-foreground mt-0.5 truncate">
                        {description}
                        {(entry.metadata as any)?.vin && entry.action !== "permanently_deleted" && (
                          <span className="ml-2 text-xs opacity-70">VIN: {(entry.metadata as any).vin}</span>
                        )}
                      </p>

                      {/* Expandable changes section */}
                      {showChanges && (
                        <div className="mt-1">
                          <button
                            onClick={() => toggleExpanded(entry.id)}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {isExpanded ? "Hide changes" : "Show changes"}
                          </button>
                          {isExpanded && (
                            <div className="mt-1 p-2 bg-muted/40 rounded-md border border-dashed">
                              {renderChanges(entry.changes)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div className="flex-shrink-0 text-right">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimestamp(entry.performed_at)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
